import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Container, Fade, Zoom, Slide } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import Logo from '../../components/Logo';
import { DASHBOARD_PATH } from '../../config';

// ==============================|| LOGO PAGE ||============================== //

// Keyframe animations
const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const glow = keyframes`
  0% {
    text-shadow: 0 0 5px rgba(112, 145, 230, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(112, 145, 230, 0.6), 0 0 30px rgba(112, 145, 230, 0.4);
  }
  100% {
    text-shadow: 0 0 5px rgba(112, 145, 230, 0.3);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Styled components
const AnimatedContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  minWidth:'100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: `linear-gradient(135deg, 
    ${theme.palette.background.default} 0%, 
    ${theme.palette.grey[50]} 50%, 
    ${theme.palette.background.default} 100%)`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, rgba(112, 145, 230, 0.1) 0%, transparent 50%),
                 radial-gradient(circle at 80% 20%, rgba(112, 145, 230, 0.1) 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  animation: `${float} 3s ease-in-out infinite`,
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '150%',
    height: '150%',
    transform: 'translate(-50%, -50%)',
    //background: 'radial-gradient(circle, rgba(112, 145, 230, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: `${glow} 2s ease-in-out infinite`,
    zIndex: -1,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: '"Playfair Display", "Georgia", serif',
  fontWeight: 300,
  letterSpacing: '0.5px',
  background: `linear-gradient(45deg, 
    ${theme.palette.text.primary} 0%, 
    #7091e6 50%, 
    ${theme.palette.text.primary} 100%)`,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${shimmer} 3s ease-in-out infinite`,
  textAlign: 'center',
  lineHeight: 1.4,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '2px',
    background: `linear-gradient(90deg, transparent, #7091e6, transparent)`,
    borderRadius: '1px',
  },
}));

const PeopleText = styled('span')(({ theme }) => ({
  fontSize: 18,
  color: '#7091e6',
  letterSpacing: 1,
  marginLeft: 6,
  marginBottom: 2,
  animation: `${glow} 2s ease-in-out infinite`,
}));

// Logo Section Component
function LogoSection() {
  return (
    <Link component={RouterLink} to={DASHBOARD_PATH} aria-label="theme-logo">
      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <Logo />
        <PeopleText>People</PeopleText>
      </Box>
    </Link>
  );
}

export default function AnimatedWelcomePage() {
  const [showContent, setShowContent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 500);

    // Redirect after 5 seconds
    const redirectTimer = setTimeout(() => {
      navigate(DASHBOARD_PATH);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(redirectTimer);
    };
  }, [navigate]); 

  return (
    <AnimatedContainer maxWidth="lg">
      <Zoom in timeout={1000}>
        <LogoContainer>
          <LogoSection />
        </LogoContainer>
      </Zoom>
      
      <Fade in={showContent} timeout={1500}>
        <Box>
          <Slide direction="up" in={showContent} timeout={1200}>
            <StyledTypography variant="h3" component="h1">
              We wish you having the best experience.
            </StyledTypography>
          </Slide>
        </Box>
      </Fade>
      
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '4px',
          height: '4px',
          backgroundColor: '#7091e6',
          borderRadius: '50%',
          animation: `${float} 4s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '70%',
          right: '15%',
          width: '6px',
          height: '6px',
          backgroundColor: '#7091e6',
          borderRadius: '50%',
          animation: `${float} 3s ease-in-out infinite 1s`,
          opacity: 0.4,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          right: '20%',
          width: '3px',
          height: '3px',
          backgroundColor: '#7091e6',
          borderRadius: '50%',
          animation: `${float} 5s ease-in-out infinite 2s`,
          opacity: 0.5,
        }}
      />
    </AnimatedContainer>
  );
}